import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, Briefcase, Star, Plus, Truck, Car, Trash2, X } from "lucide-react";
import DestinationPicker from "@/components/DestinationPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = ["Maison", "Bureau"];
const iconFor = (label) => (/^maison$/i.test(label) ? Home : /^bureau$/i.test(label) ? Briefcase : Star);

const Sheet = ({ children, onClose, testId }) => createPortal(
  <div className="fixed inset-0 z-[1200] bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose} data-testid={testId}>
    <div className="w-full sm:max-w-sm bg-[#1A1A1A] rounded-t-3xl sm:rounded-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>,
  document.body
);

const FavActions = ({ fav, onGo, onDelete, onClose }) => (
  <Sheet onClose={onClose} testId="fav-actions-sheet">
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-white font-black text-lg">{fav.label}</p>
        <p className="text-xs text-[#A0A0A0] truncate">{fav.address}</p>
      </div>
      <button onClick={onClose} className="text-white/70" data-testid="fav-actions-close"><X className="w-5 h-5" /></button>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <button onClick={() => onGo("delivery")} data-testid="fav-go-delivery"
        className="flex items-center justify-center gap-2 bg-[#D4AF37] text-black rounded-full py-3 font-black text-sm active:scale-95 transition-transform">
        <Truck className="w-4 h-4" /> Livraison
      </button>
      <button onClick={() => onGo("taxi")} data-testid="fav-go-taxi"
        className="flex items-center justify-center gap-2 bg-white text-black rounded-full py-3 font-black text-sm active:scale-95 transition-transform">
        <Car className="w-4 h-4" /> Taxi
      </button>
    </div>
    <button onClick={onDelete} data-testid="fav-delete"
      className="w-full flex items-center justify-center gap-2 text-red-400 text-xs font-bold py-2 hover:text-red-300">
      <Trash2 className="w-3.5 h-3.5" /> Retirer des favoris
    </button>
  </Sheet>
);

const FavNameStep = ({ onNext, onClose }) => {
  const [custom, setCustom] = useState("");
  return (
    <Sheet onClose={onClose} testId="fav-name-sheet">
      <p className="text-white font-black text-lg">Nouveau favori</p>
      <div className="flex gap-2">
        {PRESETS.map((p) => {
          const Icon = iconFor(p);
          return (
            <button key={p} onClick={() => onNext(p)} data-testid={`fav-preset-${p.toLowerCase()}`}
              className="flex-1 flex items-center justify-center gap-2 bg-[#2D2D2D] hover:bg-[#383838] text-white rounded-2xl py-3 font-bold transition-colors">
              <Icon className="w-4 h-4 text-[#D4AF37]" /> {p}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} maxLength={40} placeholder="Autre nom (ex : Chez Maman)"
          className="flex-1 bg-[#2D2D2D] text-white rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" data-testid="fav-custom-name" />
        <button disabled={!custom.trim()} onClick={() => onNext(custom.trim())} data-testid="fav-custom-next"
          className="bg-[#D4AF37] disabled:opacity-40 text-black rounded-2xl px-4 font-black text-sm">OK</button>
      </div>
      <p className="text-[11px] text-[#A0A0A0]">Ensuite, choisissez l'adresse sur la carte.</p>
    </Sheet>
  );
};

export default function FavoritePlaces({ pos }) {
  const navigate = useNavigate();
  const [favs, setFavs] = useState([]);
  const [active, setActive] = useState(null);
  const [naming, setNaming] = useState(false);
  const [pickLabel, setPickLabel] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/users/me/favorites`).then((r) => setFavs(r.data.favorites || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const save = async (lat, lng, address) => {
    const label = pickLabel;
    setPickLabel(null);
    try {
      await axios.post(`${API}/users/me/favorites`, { label, lat, lng, address: address || `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
      toast.success(`« ${label} » ajouté à vos favoris`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'enregistrer ce favori");
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/users/me/favorites/${active.id}`);
      setActive(null);
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const go = (kind) => {
    const q = kind === "taxi"
      ? new URLSearchParams({ dest_lat: active.lat, dest_lng: active.lng, dest_address: active.address })
      : new URLSearchParams({ delivery_lat: active.lat, delivery_lng: active.lng, delivery_address: active.address });
    navigate(`${kind === "taxi" ? "/taxi" : "/orders/new"}?${q.toString()}`);
  };

  return (
    <div data-testid="favorite-places">
      <h3 className="text-white text-sm font-semibold mb-2 px-1">Favoris</h3>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {favs.map((f) => {
          const Icon = iconFor(f.label);
          return (
            <button key={f.id} onClick={() => setActive(f)} data-testid={`fav-chip-${f.id}`}
              className="flex items-center gap-2 bg-[#2D2D2D] hover:bg-[#383838] text-white rounded-full pl-3 pr-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors active:scale-95">
              <Icon className="w-4 h-4 text-[#D4AF37]" /> {f.label.charAt(0).toUpperCase() + f.label.slice(1)}
            </button>
          );
        })}
        <button onClick={() => setNaming(true)} data-testid="fav-add-btn"
          className="flex items-center gap-1.5 border border-dashed border-[#D4AF37]/60 text-[#D4AF37] rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap hover:bg-[#D4AF37]/10 transition-colors">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>
      {active && <FavActions fav={active} onGo={go} onDelete={remove} onClose={() => setActive(null)} />}
      {naming && <FavNameStep onClose={() => setNaming(false)} onNext={(l) => { setNaming(false); setPickLabel(l); }} />}
      {pickLabel && <DestinationPicker onClose={() => setPickLabel(null)} onConfirm={save} initialCenter={pos} />}
    </div>
  );
}
